/**
 * 인앱 브라우저(WebView) 감지 유틸리티
 *
 * 카카오톡·네이버앱 등 인앱 브라우저(WebView)에서는 구글 OAuth(GSI)가
 * 정책적으로 차단(disallowed_useragent)되어 로그인이 실패한다.
 * (실제 증상: "Failed to load the Google 3P Authorization JavaScript Library.")
 *
 * 코드로 인앱 브라우저 안에서 로그인을 성공시킬 수는 없으므로,
 * 이 유틸로 인앱 브라우저를 감지한 뒤 Chrome/Safari 등 외부 브라우저로
 * 유도하는 것이 정석이다.
 */

function getUserAgent(): string {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return '';
  return navigator.userAgent;
}

/**
 * iOS(iPhone/iPad/iPod) 여부.
 * iPadOS 13+ 는 데스크톱 Safari 로 위장(Macintosh + 멀티터치)하므로 함께 처리한다.
 */
export function isIOS(): boolean {
  const ua = getUserAgent();
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return (
    /Macintosh/i.test(ua) &&
    typeof navigator !== 'undefined' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  );
}

/** Android 여부. */
export function isAndroid(): boolean {
  return /Android/i.test(getUserAgent());
}

interface InAppBrowserMatcher {
  /** 사용자에게 노출할 앱 이름 */
  name: string;
  /** UA 문자열 매칭 함수 */
  test: (ua: string) => boolean;
}

/**
 * 주요 인앱 브라우저(WebView) UA 시그니처 목록.
 * 위에서부터 순서대로 검사하며, 먼저 매칭되는 이름을 반환한다.
 */
const IN_APP_BROWSER_MATCHERS: InAppBrowserMatcher[] = [
  { name: '카카오톡', test: ua => /KAKAOTALK/i.test(ua) },
  { name: '카카오스토리', test: ua => /KAKAOSTORY/i.test(ua) },
  // 네이버앱: "NAVER(inapp; ...)" 또는 "; NAVER" 형태
  { name: '네이버', test: ua => /NAVER\(inapp|;\s*NAVER/i.test(ua) },
  { name: '인스타그램', test: ua => /Instagram/i.test(ua) },
  // 페이스북 계열: FBAN/FBAV/FB_IAB
  { name: '페이스북', test: ua => /\bFB(AN|AV|_IAB)\b|FBAN|FBAV|FB_IAB/i.test(ua) },
  // 라인: "Line/12.0.0"
  { name: '라인', test: ua => /\bLine\//i.test(ua) },
  // 밴드: "BAND/..." 또는 "; BAND"
  { name: '밴드', test: ua => /\bBAND\/|;\s*BAND/i.test(ua) },
  // 다음앱: "DaumApps/..." (네이버 웨일 Whale 등 정식 브라우저와 혼동되지 않도록 앱 시그니처만)
  { name: '다음', test: ua => /DaumApps|DaumDevice|\bDaum\//i.test(ua) },
];

/**
 * 현재 브라우저가 인앱 브라우저면 앱 이름을, 아니면 null 을 반환한다.
 * @example getInAppBrowserName() // '카카오톡' | null
 */
export function getInAppBrowserName(): string | null {
  const ua = getUserAgent();
  if (!ua) return null;
  const matched = IN_APP_BROWSER_MATCHERS.find(matcher => matcher.test(ua));
  return matched ? matched.name : null;
}

/** 현재 브라우저가 인앱 브라우저(WebView)인지 여부. */
export function isInAppBrowser(): boolean {
  return getInAppBrowserName() !== null;
}

/**
 * Android 에서 현재 URL 을 Chrome 으로 강제 오픈하기 위한 intent URL 을 생성한다.
 * `location.href` 에 대입하면 Chrome 이 열린다. (Chrome 미설치 시 스토어로 유도됨)
 * @example intent://my.jungto.org/login#Intent;scheme=https;package=com.android.chrome;end;
 */
export function getAndroidChromeIntentUrl(): string {
  if (typeof window === 'undefined') return '';
  const { host, pathname, search, hash } = window.location;
  return `intent://${host}${pathname}${search}${hash}#Intent;scheme=https;package=com.android.chrome;end;`;
}
